import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { closest, testReducedMotion, withinStops, toArray, setAttribute, toggleAttribute, withinRange } from '@nrk/dh-basis-common';

var STOPS_ATTR = 'stops';
var TARGET_STOP_ATTR = 'target-stop';
var AUTOPLAY_BLOCKED = 'autoplay-blocked';
var STATE_ATTR = 'data-state'; // Data attribute, so that it can be used on other elements
var VIEWPORT_POSITION_ATTR = 'data-viewport-position';
var READY_STATE = {
  HAVE_NOTHING: 0,
  HAVE_METADATA: 1,
  HAVE_CURRENT_DATA: 2,
  HAVE_FUTURE_DATA: 3,
  HAVE_ENOUGH_DATA: 4 // Can play through
};

// Most videos have a frame-rate of 25 fps, but not all. Use with care.
// Used to calculate target zone.
var FRAME_RATE = 25;
var isVisible = child => child.offsetWidth > 0 || child.offsetHeight > 0;

/* Ponyfill Math.sign */
function sign(val) {
  return val >= 0 ? 1 : -1;
}
function round(num, decimals) {
  var multiplier = Math.pow(10.0, decimals);
  return Math.round(num * multiplier) / multiplier;
}
class StegvisVideo extends HTMLElement {
  constructor() {
    super(...arguments);
    _defineProperty(this, "_paused", true);
    _defineProperty(this, "_disconnecting", false);
    _defineProperty(this, "_reducedMotionSeekEnabled", false);
    _defineProperty(this, "_onReadyStateChange", e => {
      if (isVisible(e.target) && e.target.readyState >= READY_STATE.HAVE_ENOUGH_DATA) {
        this._startSeek();
      }
    });
    _defineProperty(this, "_handleClick", e => {
      var btn = closest(e.target, 'button');
      if (!btn) {
        return;
      }

      /* Triggers play if autoplay was blocked */
      var videoElement = this.videoElement;
      if (videoElement !== null && videoElement !== void 0 && videoElement.paused) {
        var playPromise = videoElement.play() || Promise.resolve();
        playPromise.then(() => {
          this.autoplayBlocked = false;
        });
      }
    });
  }
  static get observedAttributes() {
    return [TARGET_STOP_ATTR, STATE_ATTR, VIEWPORT_POSITION_ATTR];
  }
  connectedCallback() {
    this.addEventListener('click', this._handleClick);
    this.videoElements.forEach(videoElement => videoElement.addEventListener('canplaythrough', this._onReadyStateChange));
  }
  disconnectedCallback() {
    this._disconnecting = true;
    this.removeEventListener('click', this._handleClick);
    this.videoElements.forEach(videoElement => videoElement.removeEventListener('canplaythrough', this._onReadyStateChange));
  }
  attributeChangedCallback(name, prevValue, newValue) {
    var hasValue = newValue !== null;
    var valueChanged = prevValue !== newValue;
    if (!valueChanged) {
      return;
    }
    if (name === STATE_ATTR) {
      this._stateChanged(prevValue, newValue);
    } else if (name === VIEWPORT_POSITION_ATTR) {
      this._viewportChanged(newValue);
    } else if (name === TARGET_STOP_ATTR) {
      hasValue && this._startSeek();
    }
  }
  _stateChanged(prevState, nextState) {
    if (nextState === 'ACTIVE') {
      if (prevState === 'NEXT') {
        this._gotoStart();
      } else if (prevState === 'PREV') {
        this._gotoEnd();
      }
      this._startSeek();
    } else {
      this._paused = true;
    }
  }
  _viewportChanged(newValue) {
    if (newValue === 'IN-VIEWPORT') {
      this._preload();
      this._startSeek();
    } else if (newValue === 'ABOVE-VIEWPORT') {
      this._paused = true;
      this._gotoEnd();
    } else if (newValue === 'BELOW-VIEWPORT') {
      this._paused = true;
      this._gotoStart();
    }
  }
  _shouldPlay() {
    var _this$videoElement;
    var inViewport = typeof this.viewportPosition === 'undefined' || this.viewportPosition === 'IN-VIEWPORT';
    return ((_this$videoElement = this.videoElement) === null || _this$videoElement === void 0 ? void 0 : _this$videoElement.readyState) >= READY_STATE.HAVE_ENOUGH_DATA && inViewport && this.state === 'ACTIVE';
  }
  _preload() {
    var videoElement = this.videoElement;
    if ((videoElement === null || videoElement === void 0 ? void 0 : videoElement.readyState) < READY_STATE.HAVE_ENOUGH_DATA) {
      videoElement === null || videoElement === void 0 ? void 0 : videoElement.load();
    }
  }
  _gotoStart() {
    if (this.autoplayBlocked) {
      return;
    }
    var videoElement = this.videoElement;
    if (videoElement && videoElement.currentTime !== 0) {
      videoElement.currentTime = 0;
    }
  }
  _gotoEnd() {
    if (this.autoplayBlocked) {
      return;
    }
    var videoElement = this.videoElement;
    var duration = videoElement === null || videoElement === void 0 ? void 0 : videoElement.duration;
    if (Number.isFinite(duration)) {
      videoElement.currentTime = duration;
    }
  }
  _enableSeekOnReducedMotion() {
    var videoElement = this.videoElement;
    if (this._reducedMotionSeekEnabled || !videoElement) {
      return Promise.resolve();
    }
    return (videoElement.play() || Promise.resolve(true)).then(() => {
      videoElement.pause();
      this._reducedMotionSeekEnabled = true;
    }).catch(e => {
      // Ignore error
    });
  }
  _startSeek() {
    if (!this._paused || !this._shouldPlay()) {
      return;
    }
    var reducedMotion = testReducedMotion();
    this._paused = false;
    var prevTime;
    var loop = timestamp => {
      var videoElement = this.videoElement;
      var targetTime = this.stops[this.targetStop];
      if (this._disconnecting || this._paused || !videoElement || !isFinite(targetTime)) {
        this._paused = true;
        videoElement === null || videoElement === void 0 ? void 0 : videoElement.pause();
        return;
      }
      if (!prevTime) {
        prevTime = timestamp;
      }
      this.invisibleVideoElements.filter(video => !video.paused).forEach(invisibleVideo => invisibleVideo.pause());
      var dt = (timestamp - prevTime) / 1000;
      var framesPerTick = Math.max(Math.ceil(Math.abs(dt * FRAME_RATE)), 1);
      var buffer = 0.5 * framesPerTick / FRAME_RATE;
      var currentTime = videoElement.currentTime;
      var atTargetStop = withinRange(currentTime, targetTime - buffer, targetTime + buffer);
      if (atTargetStop) {
        if (videoElement.currentTime !== targetTime) {
          videoElement.currentTime = targetTime;
        }
        this._paused = true;
        videoElement.pause();
      } else if (reducedMotion) {
        this._enableSeekOnReducedMotion().then(() => {
          videoElement.currentTime = targetTime;
          this._paused = true;
        });
      } else {
        var speed = this._calculateSpeed(currentTime);
        var direction = sign(targetTime - currentTime);
        if (this.autoplayBlocked) ; else if (direction === 1) {
          // Use normal playback for forward playback
          videoElement.playbackRate = speed;
          if (videoElement.paused) {
            var playPromise = videoElement.play() || Promise.resolve();
            playPromise.catch(e => {
              console.error(e);
              this.autoplayBlocked = true;
            });
          }
        } else {
          // Use seeking for reverse playback:
          // https://github.com/whatwg/html/issues/3754
          videoElement.pause();
          var nextTime = round(currentTime + dt * speed * direction, 2.0);
          if (currentTime !== nextTime) {
            videoElement.currentTime = nextTime;
          }
        }
        prevTime = timestamp;
        requestAnimationFrame(loop);
      }
    };
    requestAnimationFrame(loop);
  }
  _calculateSpeed(currentTime) {
    return withinStops(currentTime, this.stops, this.targetStop, 1) ? 1 : 3;
  }
  get videoElement() {
    return this.videoElements.find(isVisible);
  }
  get invisibleVideoElements() {
    return this.videoElements.filter(child => !isVisible(child));
  }
  get videoElements() {
    return toArray(this.children).filter(child => child.nodeName === 'VIDEO');
  }
  get stops() {
    var stopString = this.getAttribute(STOPS_ATTR) || '';
    return stopString.replace(/[[\]]/g, '').split(',').map(s => s.trim()).filter(Boolean).map(s => Number(s));
  }
  set stops(val) {
    this.setAttribute(STOPS_ATTR, val.map(num => num.toFixed(2)).join(','));
  }
  get targetStop() {
    return Number(this.getAttribute(TARGET_STOP_ATTR) || 0);
  }
  set targetStop(val) {
    this.setAttribute(TARGET_STOP_ATTR, val);
  }
  get state() {
    return this.getAttribute(STATE_ATTR);
  }
  set state(val) {
    setAttribute(this, STATE_ATTR, val);
  }
  get viewportPosition() {
    return this.getAttribute(VIEWPORT_POSITION_ATTR);
  }
  set viewportPosition(val) {
    this.setAttribute(VIEWPORT_POSITION_ATTR, val);
  }
  get autoplayBlocked() {
    return this.hasAttribute(AUTOPLAY_BLOCKED) || false;
  }
  set autoplayBlocked(val) {
    toggleAttribute(this, AUTOPLAY_BLOCKED, val);
  }
}

export { StegvisVideo as default };
//# sourceMappingURL=index.esm.js.map
